(() => {
  'use strict';

  if (window.__SCARVELA_SUBSCRIPTION_PAYMENT_ADAPTER_V1__) return;
  window.__SCARVELA_SUBSCRIPTION_PAYMENT_ADAPTER_V1__ = '2026-08-26.2';

  const PRODUCT_ROOT_SELECTOR = '[data-product-root]';
  const CART_ADD_FORM_SELECTOR = 'form[action*="/cart/add"]';
  const SUBSCRIPTION_CONTROL_SELECTOR = [
    'input[name="subscription_enabled"][data-selling-plan-id]',
    'input[name="purchase_type"][value="subscription"][data-selling-plan-id]'
  ].join(',');
  const PURCHASE_CONTROL_SELECTOR = [
    'input[name="subscription_enabled"][data-selling-plan-id]:checked',
    'input[name="purchase_type"][value="subscription"][data-selling-plan-id]:checked'
  ].join(',');
  const PROVIDER_FORM_SELECTOR = [
    '#gfouk-product-form',
    '.xy-custom-button-pannel form[action*="/cart/add"]',
    '.yx-wallet-buttons form[action*="/cart/add"]',
    '.yx-subscription-component form[action*="/cart/add"]',
    'form[action*="/cart/add"][data-scarvela-payment-form]'
  ].join(',');
  const PAYMENT_BUTTON_SELECTOR = '#yx_checkout, #yx_klarna_checkout, #yx_paypal_checkout';
  const WALLET_CONTAINER_SELECTOR = '#google-button-container, #apple-button-container';
  const WALLET_VARIANT_NODE_SELECTOR = '[data-variant-id], button, [role="button"]';

  let syncTimer = 0;

  const normalizeId = (value) => String(value == null ? '' : value)
    .replace(/^gid:\/\/shopify\/[^/]+\//, '')
    .trim();

  const productData = () => (
    window.yx_product && typeof window.yx_product === 'object'
      ? window.yx_product
      : { variants: [] }
  );

  const productRoot = () => document.querySelector(PRODUCT_ROOT_SELECTOR) || document;
  const hasProductContext = () => (
    Boolean(document.querySelector(PRODUCT_ROOT_SELECTOR)) &&
    Array.isArray(productData().variants) &&
    productData().variants.length > 0
  );

  const selectedVariantId = (root, preferredVariantId) => normalizeId(
    preferredVariantId ||
    root.querySelector('input[name="bundle-choice"]:checked')?.value ||
    root.querySelector('#gfouk-product-form [data-variant-id]')?.value ||
    root.querySelector('#gfouk-product-form input[name="id"]')?.value ||
    ''
  );

  const selectedVariant = (root, preferredVariantId) => {
    const id = selectedVariantId(root, preferredVariantId);
    return (productData().variants || []).find((variant) => normalizeId(variant?.id) === id) || null;
  };

  const allocationFor = (variant, sellingPlanId) => {
    const planId = normalizeId(sellingPlanId);
    if (!variant || !planId || !Array.isArray(variant.selling_plan_allocations)) return null;
    return variant.selling_plan_allocations.find(
      (allocation) => normalizeId(allocation?.selling_plan_id) === planId
    ) || null;
  };

  const managedCartAddForms = (root) => {
    if (!hasProductContext()) return [];
    const forms = new Set();
    const add = (form) => {
      if (form instanceof HTMLFormElement && form.matches(CART_ADD_FORM_SELECTOR)) forms.add(form);
    };

    root.querySelectorAll(PROVIDER_FORM_SELECTOR).forEach(add);
    document.querySelectorAll([
      '.xy-custom-button-pannel form[action*="/cart/add"]',
      '.yx-wallet-buttons form[action*="/cart/add"]',
      'form[action*="/cart/add"][data-scarvela-payment-form]'
    ].join(',')).forEach(add);

    const currentProductId = normalizeId(productData().id);
    if (currentProductId) {
      document.querySelectorAll(`${CART_ADD_FORM_SELECTOR}[data-product-id], [data-product-id] ${CART_ADD_FORM_SELECTOR}`)
        .forEach((form) => {
          const owner = form.matches('[data-product-id]') ? form : form.closest('[data-product-id]');
          if (normalizeId(owner?.dataset?.productId) === currentProductId) add(form);
        });
    }
    return Array.from(forms);
  };

  const subscriptionStatus = (root) => {
    const component = root.querySelector('.yx-subscription-component');
    if (!component) return null;
    let status = component.querySelector('[data-scarvela-subscription-status]');
    if (!status) {
      status = document.createElement('p');
      status.className = 'scarvela-subscription-status';
      status.dataset.scarvelaSubscriptionStatus = 'true';
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      status.hidden = true;
      component.appendChild(status);
    }
    return status;
  };

  const syncSubscriptionAvailability = (root, variant) => {
    const controls = Array.from(root.querySelectorAll(SUBSCRIPTION_CONTROL_SELECTOR));
    let availableCount = 0;
    let selectedUnavailable = false;

    controls.forEach((control) => {
      const available = Boolean(allocationFor(variant, control.dataset.sellingPlanId));
      if (available) {
        availableCount += 1;
        if (control.dataset.scarvelaDisabledByAllocation === 'true') {
          control.disabled = false;
          control.removeAttribute('aria-disabled');
          delete control.dataset.scarvelaDisabledByAllocation;
        }
        return;
      }

      if (control.checked) {
        selectedUnavailable = true;
        control.checked = false;
      }
      if (!control.disabled) {
        control.disabled = true;
        control.dataset.scarvelaDisabledByAllocation = 'true';
      }
      control.setAttribute('aria-disabled', 'true');
    });

    const hasAvailableSelection = controls.some((control) => (
      control.checked && Boolean(allocationFor(variant, control.dataset.sellingPlanId))
    ));
    if (selectedUnavailable && !hasAvailableSelection) {
      const oneTime = root.querySelector('input[name="purchase_type"][value="onetime"]');
      if (oneTime && !oneTime.disabled) oneTime.checked = true;
    }

    const unavailable = controls.length > 0 && (availableCount === 0 || selectedUnavailable);
    const status = subscriptionStatus(root);
    if (status) {
      status.hidden = !unavailable;
      status.textContent = unavailable
        ? 'Subscription is unavailable for this bundle. One-time purchase has been selected.'
        : '';
    }
    return { unavailable, availableCount, selectedUnavailable };
  };

  const currentPurchase = (root, preferredVariantId) => {
    const variant = selectedVariant(root, preferredVariantId);
    const controls = Array.from(root.querySelectorAll(PURCHASE_CONTROL_SELECTOR));
    for (const control of controls) {
      const sellingPlanId = normalizeId(control.dataset.sellingPlanId);
      const allocation = allocationFor(variant, sellingPlanId);
      if (allocation) {
        return { variant, control, sellingPlanId, allocation, isSubscription: true };
      }
    }
    return { variant, control: null, sellingPlanId: '', allocation: null, isSubscription: false };
  };

  const finiteCents = (value, fallback) => {
    const cents = Number(value);
    return Number.isFinite(cents) && cents >= 0 ? cents : fallback;
  };

  const formatMoney = (cents) => {
    const currency = window.Shopify?.currency?.active || 'USD';
    const locale = document.documentElement.lang || 'en-US';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
    } catch (error) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };

  const selectedBundleRadio = (root, variantId) => Array.from(
    root.querySelectorAll('input[name="bundle-choice"]')
  ).find((radio) => normalizeId(radio.value) === normalizeId(variantId)) || null;

  const pricingFor = (state, root) => {
    if (!state.variant) return null;
    const variantPrice = finiteCents(state.variant.price, 0);
    const variantCompare = finiteCents(state.variant.compare_at_price, variantPrice);

    if (!state.isSubscription) {
      const radio = selectedBundleRadio(root, state.variant.id);
      return {
        price: variantPrice,
        compareAt: variantCompare,
        formattedPrice: radio?.dataset.priceFormatted || formatMoney(variantPrice),
        formattedCompareAt: radio?.dataset.compareFormatted || formatMoney(variantCompare)
      };
    }

    const allocationPrice = finiteCents(state.allocation.price, variantPrice);
    const allocationCompareAt = finiteCents(
      state.allocation.compare_at_price,
      variantPrice
    );
    return {
      price: allocationPrice,
      compareAt: Math.max(allocationPrice, allocationCompareAt),
      formattedPrice: formatMoney(allocationPrice),
      formattedCompareAt: formatMoney(Math.max(allocationPrice, allocationCompareAt))
    };
  };

  const syncBundleCardPrices = (root, state) => {
    const activePlanId = state.isSubscription ? state.sellingPlanId : '';
    root.querySelectorAll('input[name="bundle-choice"]').forEach((radio) => {
      const variant = (productData().variants || []).find(
        (item) => normalizeId(item?.id) === normalizeId(radio.value)
      );
      const card = radio.closest('[data-variant-card]') || radio.parentElement;
      const priceElement = card?.querySelector('[data-bundle-price]');
      const compareElement = card?.querySelector('[data-bundle-compare]');
      const averageElement = card?.querySelector('[data-bundle-average]');
      if (!variant || !priceElement || !compareElement) return;

      const oneTimePrice = finiteCents(variant.price, finiteCents(radio.dataset.price, 0));
      const oneTimeCompare = finiteCents(
        variant.compare_at_price,
        finiteCents(radio.dataset.compare, oneTimePrice)
      );
      const allocation = activePlanId ? allocationFor(variant, activePlanId) : null;
      const price = allocation
        ? finiteCents(allocation.price, oneTimePrice)
        : oneTimePrice;
      const allocationCompare = allocation
        ? finiteCents(allocation.compare_at_price, oneTimePrice)
        : oneTimeCompare;
      const compareAt = Math.max(price, allocationCompare);

      priceElement.textContent = allocation
        ? formatMoney(price)
        : (radio.dataset.priceFormatted || formatMoney(price));
      compareElement.textContent = allocation
        ? formatMoney(compareAt)
        : (radio.dataset.compareFormatted || formatMoney(compareAt));
      if (averageElement) {
        const packCount = Math.max(1, Number(radio.dataset.packCount) || 1);
        averageElement.textContent = formatMoney(price / packCount);
        averageElement.dataset.scarvelaAverageCents = String(price / packCount);
      }
      priceElement.dataset.scarvelaPriceCents = String(price);
      compareElement.dataset.scarvelaCompareCents = String(compareAt);
      compareElement.hidden = compareAt <= price;
      card.dataset.scarvelaPurchaseType = allocation ? 'subscription' : 'onetime';
    });
  };

  const syncDisplayedPrice = (root, state) => {
    const pricing = pricingFor(state, root);
    if (!pricing) return;
    const discount = pricing.compareAt > 0
      ? Math.max(0, Math.min(100, Math.round((pricing.compareAt - pricing.price) * 100 / pricing.compareAt)))
      : 0;

    root.querySelectorAll('[data-product-price]').forEach((element) => {
      element.textContent = pricing.formattedPrice;
      element.dataset.scarvelaPriceCents = String(pricing.price);
    });
    root.querySelectorAll('[data-product-compare]').forEach((element) => {
      element.textContent = pricing.formattedCompareAt;
      element.dataset.scarvelaCompareCents = String(pricing.compareAt);
    });
    root.querySelectorAll('[data-product-save]').forEach((element) => {
      element.textContent = `SAVE ${discount}%`;
      element.dataset.scarvelaSavePercent = String(discount);
    });
    root.querySelectorAll('[data-urgency-price]').forEach((element) => {
      element.textContent = pricing.formattedPrice;
      element.dataset.scarvelaPriceCents = String(pricing.price);
    });
    root.querySelectorAll('[data-urgency-compare]').forEach((element) => {
      element.textContent = `(Was ${pricing.formattedCompareAt})`;
      element.dataset.scarvelaCompareCents = String(pricing.compareAt);
    });
  };

  const setVariantData = (root, variantId) => {
    const id = normalizeId(variantId);
    if (!id) return;

    root.querySelectorAll(PAYMENT_BUTTON_SELECTOR).forEach((button) => {
      button.dataset.variantId = id;
    });

    root.querySelectorAll(WALLET_CONTAINER_SELECTOR).forEach((container) => {
      container.dataset.variantId = id;
      container.querySelectorAll(WALLET_VARIANT_NODE_SELECTOR).forEach((element) => {
        element.dataset.variantId = id;
      });
    });
  };

  const setFormSellingPlan = (form, sellingPlanId) => {
    const desired = normalizeId(sellingPlanId);
    const existing = Array.from(form.querySelectorAll('input[name="selling_plan"]'));
    if (!desired) {
      existing.forEach((input) => input.remove());
      return;
    }
    if (existing.length === 1 && normalizeId(existing[0].value) === desired) {
      existing[0].type = 'hidden';
      return;
    }
    existing.forEach((input) => input.remove());
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'selling_plan';
    input.value = desired;
    input.dataset.scarvelaSellingPlan = 'true';
    form.appendChild(input);
  };

  const syncSellingPlanForms = (state) => {
    if (!hasProductContext()) return null;
    const current = state || currentPurchase(productRoot());
    const sellingPlanId = current.isSubscription ? current.sellingPlanId : '';
    managedCartAddForms(productRoot()).forEach((form) => {
      setFormSellingPlan(form, sellingPlanId);
    });
    return sellingPlanId || null;
  };

  const sync = (preferredVariantId) => {
    if (!hasProductContext()) return { variant: null, isSubscription: false };
    const root = productRoot();
    let state = currentPurchase(root, preferredVariantId);
    if (!state.variant) {
      syncSellingPlanForms(state);
      return state;
    }

    const availability = syncSubscriptionAvailability(root, state.variant);
    state = currentPurchase(root, preferredVariantId);
    state.subscriptionUnavailable = availability.unavailable;

    const variantId = normalizeId(state.variant.id);
    const productFormVariant = root.querySelector('#gfouk-product-form [data-variant-id]');
    if (productFormVariant && normalizeId(productFormVariant.value) !== variantId) {
      productFormVariant.value = variantId;
    }
    setVariantData(root, variantId);
    syncSellingPlanForms(state);
    syncDisplayedPrice(root, state);
    syncBundleCardPrices(root, state);
    root.dataset.scarvelaPurchaseType = state.isSubscription ? 'subscription' : 'onetime';
    if (state.isSubscription) root.dataset.scarvelaSellingPlanId = state.sellingPlanId;
    else delete root.dataset.scarvelaSellingPlanId;
    return state;
  };

  const scheduleSync = (preferredVariantId) => {
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(() => sync(preferredVariantId), 0);
  };

  window.yxGetManagedCartAddForms = () => managedCartAddForms(productRoot());
  window.yxSyncSellingPlanForms = () => {
    const root = productRoot();
    const state = currentPurchase(root);
    if (state.variant) syncSubscriptionAvailability(root, state.variant);
    return syncSellingPlanForms(currentPurchase(root));
  };
  window.scarvelaSyncSubscriptionPayment = sync;

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    if (target.matches('input[name="bundle-choice"]')) {
      const state = sync(target.value);
      document.dispatchEvent(new CustomEvent('variant:change', {
        detail: {
          variant: state.variant,
          variantId: normalizeId(state.variant?.id || target.value),
          source: 'scarvela-bundle-choice'
        }
      }));
      window.setTimeout(() => sync(target.value), 100);
      window.setTimeout(() => sync(target.value), 400);
      return;
    }

    if (target.matches('input[name="subscription_enabled"], [data-purchase-type]')) {
      sync();
      window.setTimeout(sync, 100);
      window.setTimeout(sync, 400);
    }
  });

  document.addEventListener('purchase-type:changed', () => scheduleSync());
  document.addEventListener('submit', () => syncSellingPlanForms(currentPurchase(productRoot())), true);

  const init = () => {
    if (!hasProductContext()) return;
    sync();
    window.setTimeout(sync, 100);
    window.setTimeout(sync, 400);
    if (!document.body) return;
    new MutationObserver((mutations) => {
      const hasRelevantAddition = mutations.some((mutation) => Array.from(mutation.addedNodes).some((node) => (
        node.nodeType === Node.ELEMENT_NODE && (
          node.closest?.(WALLET_CONTAINER_SELECTOR) ||
          node.matches?.(`${CART_ADD_FORM_SELECTOR}, ${WALLET_CONTAINER_SELECTOR}`) ||
          node.querySelector?.(`${CART_ADD_FORM_SELECTOR}, ${WALLET_CONTAINER_SELECTOR}`)
        )
      )));
      if (hasRelevantAddition) scheduleSync();
    }).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
